import { useState } from "react";
import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BoardField, FieldOption, FieldType, LabelColor } from "@kandoo/shared";
import { api } from "../api/client";
import { Modal } from "./Modal";
import { ConfirmButton } from "./ConfirmButton";
import { FIELD_TYPE_LABELS } from "../util/fields";
import { LABEL_COLOR_KEYS, labelColor } from "../util/labelColors";

interface BoardFieldsDialogProps {
  boardId: string;
  onClose: () => void;
}

/**
 * Add, rename, reorder and delete a board's custom fields, edit a dropdown's
 * choices, and pick which fields show on card faces. Open to editors, like
 * the label palette.
 */
export function BoardFieldsDialog({ boardId, onClose }: BoardFieldsDialogProps) {
  const qc = useQueryClient();
  const { data: board } = useQuery({
    queryKey: ["board", boardId],
    queryFn: () => api.boards.get(boardId),
  });
  const fields = [...(board?.fields ?? [])].sort((a, b) => a.position - b.position);
  // how many cards on the board have a value for each field (shown before delete)
  const usage = new Map<string, number>();
  for (const l of board?.lists ?? [])
    for (const c of l.cards)
      for (const id of Object.keys(c.fieldValues ?? {})) usage.set(id, (usage.get(id) ?? 0) + 1);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["board", boardId] });
    qc.invalidateQueries({ queryKey: ["card"] });
  };
  const create = useMutation({
    mutationFn: (b: { name: string; type: FieldType }) =>
      api.fields.create(boardId, {
        ...b,
        // a dropdown with no choices is useless; start it with two to rename
        options:
          b.type === "select"
            ? [
                { name: "Option 1", color: "blue" },
                { name: "Option 2", color: "green" },
              ]
            : undefined,
      }),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof api.fields.update>[1] }) =>
      api.fields.update(id, patch),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.fields.delete(id),
    onSuccess: invalidate,
  });

  /** Swap a field with its neighbor (positions are dense ints). */
  function move(index: number, dir: -1 | 1) {
    const other = fields[index + dir];
    const field = fields[index];
    if (!other) return;
    update.mutate({ id: field.id, patch: { position: other.position } });
    update.mutate({ id: other.id, patch: { position: field.position } });
  }

  return (
    <Modal onClose={onClose} ariaLabel="Custom fields">
      <div className="p-5 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Custom fields</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={16} />
          </button>
        </div>

        {fields.length === 0 && (
          <p className="mb-4 text-sm text-slate-500">
            Fields add structured data to every card on this board, like a priority,
            an estimate or a link.
          </p>
        )}

        <ul className="space-y-3">
          {fields.map((f, i) => (
            <FieldRow
              key={f.id}
              field={f}
              usage={usage.get(f.id) ?? 0}
              first={i === 0}
              last={i === fields.length - 1}
              onMove={(dir) => move(i, dir)}
              onUpdate={(patch) => update.mutate({ id: f.id, patch })}
              onDelete={() => remove.mutate(f.id)}
            />
          ))}
        </ul>

        <NewField onCreate={(b) => create.mutate(b)} />
      </div>
    </Modal>
  );
}

function FieldRow({
  field,
  usage,
  first,
  last,
  onMove,
  onUpdate,
  onDelete,
}: {
  field: BoardField;
  usage: number;
  first: boolean;
  last: boolean;
  onMove: (dir: -1 | 1) => void;
  onUpdate: (patch: { name?: string; options?: FieldOption[]; showOnCard?: boolean }) => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(field.name);
  return (
    <li className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-center gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => {
            const n = name.trim();
            if (n && n !== field.name) onUpdate({ name: n });
            else setName(field.name);
          }}
          onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
          maxLength={50}
          aria-label="Field name"
          className="min-w-0 flex-1 rounded border border-slate-300 bg-[var(--surface)] px-2 py-1 text-sm font-medium"
        />
        <span className="shrink-0 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
          {FIELD_TYPE_LABELS[field.type]}
        </span>
        <button
          type="button"
          disabled={first}
          onClick={() => onMove(-1)}
          aria-label="Move up"
          className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
        >
          <ArrowUp size={14} />
        </button>
        <button
          type="button"
          disabled={last}
          onClick={() => onMove(1)}
          aria-label="Move down"
          className="rounded p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
        >
          <ArrowDown size={14} />
        </button>
      </div>

      {field.type === "select" && (
        <OptionsEditor options={field.options} onChange={(options) => onUpdate({ options })} />
      )}

      <div className="mt-2 flex items-center justify-between text-xs">
        <label className="flex items-center gap-1.5 text-slate-600">
          <input
            type="checkbox"
            checked={field.showOnCard}
            onChange={(e) => onUpdate({ showOnCard: e.target.checked })}
          />
          Show on card faces
        </label>
        <ConfirmButton
          onConfirm={onDelete}
          title="Delete this field from every card"
          className="text-slate-400 hover:text-red-600"
          confirmClassName="rounded bg-red-600 px-1.5 py-0.5 text-white"
          confirmLabel={
            usage > 0
              ? `Delete it and its value on ${usage} ${usage === 1 ? "card" : "cards"}?`
              : "Delete field?"
          }
        >
          Delete field
        </ConfirmButton>
      </div>
    </li>
  );
}

/** Dropdown choices: rename in place, click the dot to cycle color, × to remove. */
function OptionsEditor({
  options,
  onChange,
}: {
  options: FieldOption[];
  onChange: (options: FieldOption[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const nextColor = (c: LabelColor): LabelColor =>
    LABEL_COLOR_KEYS[(LABEL_COLOR_KEYS.indexOf(c) + 1) % LABEL_COLOR_KEYS.length];
  function add() {
    const n = draft.trim();
    if (!n) return;
    const used = new Set(options.map((o) => o.color));
    const color = LABEL_COLOR_KEYS.find((c) => !used.has(c)) ?? "gray";
    // no id: the server assigns one
    onChange([...options, { name: n, color } as FieldOption]);
    setDraft("");
  }
  return (
    <div className="mt-2 space-y-1.5 pl-1">
      {options.map((o, i) => (
        <OptionRow
          key={o.id}
          option={o}
          onRename={(name) =>
            onChange(options.map((x, j) => (j === i ? { ...x, name } : x)))
          }
          onRecolor={() =>
            onChange(options.map((x, j) => (j === i ? { ...x, color: nextColor(x.color) } : x)))
          }
          onRemove={() => onChange(options.filter((_, j) => j !== i))}
        />
      ))}
      <div className="flex items-center gap-2">
        <Plus size={14} className="shrink-0 text-slate-400" />
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          onBlur={add}
          maxLength={50}
          placeholder="Add a choice"
          aria-label="Add a choice"
          className="min-w-0 flex-1 rounded border border-slate-300 bg-[var(--surface)] px-2 py-0.5 text-sm"
        />
      </div>
    </div>
  );
}

function OptionRow({
  option,
  onRename,
  onRecolor,
  onRemove,
}: {
  option: FieldOption;
  onRename: (name: string) => void;
  onRecolor: () => void;
  onRemove: () => void;
}) {
  const [name, setName] = useState(option.name);
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onRecolor}
        title="Change color"
        aria-label={`Change color of ${option.name}`}
        className={`h-3.5 w-3.5 shrink-0 rounded-full ${labelColor(option.color).swatch}`}
      />
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => {
          const n = name.trim();
          if (n && n !== option.name) onRename(n);
          else setName(option.name);
        }}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
        maxLength={50}
        aria-label="Choice name"
        className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-2 py-0.5 text-sm hover:border-slate-200 focus:border-slate-300"
      />
      <button
        type="button"
        onClick={onRemove}
        title="Remove choice (clears it on cards that use it)"
        aria-label={`Remove ${option.name}`}
        className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-red-600"
      >
        <X size={12} />
      </button>
    </div>
  );
}

function NewField({ onCreate }: { onCreate: (b: { name: string; type: FieldType }) => void }) {
  const [name, setName] = useState("");
  const [type, setType] = useState<FieldType>("text");
  const submit = () => {
    const n = name.trim();
    if (!n) return;
    onCreate({ name: n, type });
    setName("");
  };
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-4"
    >
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={50}
        placeholder="New field name"
        aria-label="New field name"
        className="min-w-0 flex-1 rounded border border-slate-300 bg-[var(--surface)] px-2 py-1 text-sm"
      />
      <select
        value={type}
        onChange={(e) => setType(e.target.value as FieldType)}
        aria-label="Field type"
        className="rounded border border-slate-300 bg-[var(--surface)] px-2 py-1 text-sm"
      >
        {(Object.keys(FIELD_TYPE_LABELS) as FieldType[]).map((t) => (
          <option key={t} value={t}>
            {FIELD_TYPE_LABELS[t]}
          </option>
        ))}
      </select>
      <button
        type="submit"
        disabled={!name.trim()}
        className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
      >
        Add field
      </button>
    </form>
  );
}
